<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import GroupMembersModal from './GroupMembersModal.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const PREVIEW_LIMIT = 3;

const showMembersModal = ref(false);

const groupTitle = computed(() => {
  return props.conversation.group_title || t('CONVERSATION.GROUP.DEFAULT_TITLE');
});

const primaryContact = computed(() => props.contact || {});

const additionalMembers = computed(() => {
  return (props.conversation.group_contacts || [])
    .map(groupContact => groupContact.contact)
    .filter(Boolean);
});

const previewMembers = computed(() => {
  const previewSize = primaryContact.value.id
    ? PREVIEW_LIMIT - 1
    : PREVIEW_LIMIT;
  return additionalMembers.value.slice(0, previewSize);
});

const memberCount = computed(() => {
  return additionalMembers.value.length + (primaryContact.value.id ? 1 : 0);
});

const hasMoreMembers = computed(() => memberCount.value > PREVIEW_LIMIT);

const memberCountLabel = computed(() =>
  t('CONVERSATION.GROUP.MEMBER_COUNT', { count: memberCount.value })
);
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-3 text-left rtl:text-right">
      <div class="flex items-center justify-center w-12 h-12 rounded-full bg-n-alpha-2">
        <span class="i-lucide-users text-xl text-n-slate-11" />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <h3 class="flex-shrink max-w-full min-w-0 my-0 text-base break-words text-n-slate-12">
          {{ groupTitle }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ memberCountLabel }}
        </p>
      </div>

      <div class="flex flex-col w-full gap-2">
        <h4 class="text-xs font-semibold uppercase text-n-slate-10">
          {{ $t('CONVERSATION.GROUP.MEMBERS') }}
        </h4>

        <div
          v-if="primaryContact.id"
          class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1 border border-n-weak"
        >
          <Avatar
            :src="primaryContact.thumbnail"
            :name="primaryContact.name"
            :size="32"
            rounded-full
          />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1">
              <span class="block text-sm font-medium truncate text-n-slate-12">
                {{ primaryContact.name }}
              </span>
              <span
                v-tooltip="$t('CONVERSATION.GROUP.PRIMARY_CONTACT')"
                class="i-lucide-star text-n-amber-9 text-xs"
              />
            </div>
            <span
              v-if="primaryContact.identifier"
              class="text-xs text-n-slate-10 truncate block"
            >
              {{ primaryContact.identifier }}
            </span>
          </div>
        </div>

        <div
          v-for="member in previewMembers"
          :key="member.id"
          class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-1 border border-n-weak"
        >
          <Avatar
            :src="member.thumbnail"
            :name="member.name"
            :size="32"
            rounded-full
          />
          <div class="flex-1 min-w-0">
            <span class="block text-sm font-medium truncate text-n-slate-12">
              {{ member.name }}
            </span>
            <span
              v-if="member.identifier"
              class="text-xs text-n-slate-10 truncate block"
            >
              {{ member.identifier }}
            </span>
          </div>
        </div>

        <button
          v-if="hasMoreMembers"
          type="button"
          class="w-full py-2 text-sm font-medium text-center rounded-lg text-n-brand hover:bg-n-alpha-2"
          @click="showMembersModal = true"
        >
          {{
            $t('CONVERSATION.GROUP.VIEW_ALL_MEMBERS', {
              count: memberCount,
            })
          }}
        </button>
      </div>
    </div>

    <GroupMembersModal
      v-model:show="showMembersModal"
      :conversation="conversation"
      :contact="primaryContact"
    />
  </div>
</template>
